import { Component, Input, Output, EventEmitter, signal, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ProductDetail, ProductAddon, AddToCartPayload } from '../../core/models/interfaces-general';
import { ProductAddonSelectorComponent } from './product-addon-selector';
import { CartService } from '../../services/cart.service';
import { DrawerService } from '../../services/drawer-service';
import { CarritoService } from '../../services/carrito-service';
import { Constante } from '../../constante';

export interface TimeSlotConfig {
  id: string;
  label: string;
  maxHour: number; // Hora límite de compra (ej. 12 para el turno que acaba a la 1pm)
}

@Component({
  selector: 'app-product-buy-options',
  standalone: true,
  imports: [CommonModule, FormsModule, ProductAddonSelectorComponent],
  template: `
    <div class="flex flex-col gap-5">
      <!-- TÍTULO Y PRECIO BASE -->
      <div>
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-800">{{ product.title }}</h1>
        <div class="text-rose-500 font-extrabold text-2xl mt-1">
          S/ {{ product.price.toFixed(2) }}
        </div>
      </div>

      <!-- COMPLEMENTOS -->
      <app-product-addon-selector 
        [addons]="availableAddons" 
        (selectionChange)="selectedAddons.set($event)"
      ></app-product-addon-selector>

      <!-- FECHA DE ENTREGA -->
      <div class="flex flex-col gap-2">
        <label class="text-xs font-semibold text-gray-600">Selecciona una fecha de entrega</label>
        
        <div class="grid grid-cols-3 gap-2">
          <button 
            (click)="selectDateOption('today')"
            [class.bg-rose-500]="dateOption() === 'today'"
            [class.text-white]="dateOption() === 'today'"
            [class.bg-gray-100]="dateOption() !== 'today'"
            [class.text-gray-700]="dateOption() !== 'today'"
            class="py-2.5 px-3 rounded-2xl text-xs font-bold text-center border border-transparent transition-all cursor-pointer"
          >
            Para Hoy
          </button>

          <button 
            (click)="selectDateOption('tomorrow')"
            [class.bg-rose-500]="dateOption() === 'tomorrow'"
            [class.text-white]="dateOption() === 'tomorrow'"
            [class.bg-gray-100]="dateOption() !== 'tomorrow'"
            [class.text-gray-700]="dateOption() !== 'tomorrow'"
            class="py-2.5 px-3 rounded-2xl text-xs font-bold text-center border border-transparent transition-all cursor-pointer"
          >
            Para Mañana
          </button>

          <button 
            (click)="selectDateOption('custom')"
            [class.bg-rose-500]="dateOption() === 'custom'"
            [class.text-white]="dateOption() === 'custom'"
            [class.bg-gray-100]="dateOption() !== 'custom'"
            [class.text-gray-700]="dateOption() !== 'custom'"
            class="py-2.5 px-3 rounded-2xl text-xs font-bold text-center border border-transparent transition-all cursor-pointer"
          >
            Otra Fecha
          </button>
        </div>

        <!-- INPUT PICKER SI SELECCIONA OTRA FECHA -->
        <div *ngIf="dateOption() === 'custom'" class="mt-1">
          <input 
            type="date" 
            [min]="todayStr"
            [ngModel]="customDate()"
            (ngModelChange)="onCustomDateChange($event)"
            class="w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2 text-xs font-semibold text-gray-700 focus:outline-none focus:border-rose-400"
          />
        </div>
      </div>

      <!-- HORARIO -->
      <div class="flex flex-col gap-2">
        <label class="text-xs font-semibold text-gray-600">Selecciona un rango horario</label>
        
        <div class="grid grid-cols-3 gap-2">
          <button 
            *ngFor="let slot of availableTimeSlots()"
            (click)="selectedTimeSlot.set(slot.id)"
            [class.bg-rose-400]="selectedTimeSlot() === slot.id"
            [class.text-white]="selectedTimeSlot() === slot.id"
            [class.bg-gray-100]="selectedTimeSlot() !== slot.id"
            [class.text-gray-600]="selectedTimeSlot() !== slot.id"
            class="py-2.5 rounded-2xl text-xs font-bold transition-all cursor-pointer"
          >
            {{ slot.label }}
          </button>
        </div>

        <!-- ADVERTENCIA SI NO HAY HORARIOS PARA HOY -->
        <p *ngIf="availableTimeSlots().length === 0" class="text-xs text-rose-500 font-medium mt-1">
          Ya no hay rangos horarios disponibles para hoy. Selecciona otra fecha.
        </p>
      </div>

      <!-- CANTIDAD Y PRECIO FINAL -->
      <div class="flex items-center justify-between bg-gray-50 p-3 rounded-2xl border border-gray-100">
        <span class="text-sm font-semibold text-gray-600">Cantidad:</span>
        <div class="flex items-center gap-3 bg-white px-3 py-1 rounded-full border">
          <button (click)="changeQuantity(-1)" class="font-bold text-gray-500 px-1 cursor-pointer">-</button>
          <span class="font-bold text-sm">{{ quantity() }}</span>
          <button (click)="changeQuantity(1)" class="font-bold text-gray-500 px-1 cursor-pointer">+</button>
        </div>
      </div>

      <div class="flex items-baseline justify-between pt-2">
        <span class="text-sm font-bold text-gray-700">Precio final:</span>
        <span class="text-2xl font-black text-rose-500">S/ {{ calculateTotal().toFixed(2) }}</span>
      </div>

      <!-- BOTONES DE ACCIÓN -->
      <div class="grid grid-cols-2 gap-3 mt-2">
        <button type="button" class="py-3 px-4 rounded-2xl border border-rose-500 text-rose-500 font-bold text-xs uppercase hover:bg-rose-50 cursor-pointer"
        (click)="onAddToCart()">
          Seguir comprando
        </button>
        <button type="button" 
          (click)="goToPay()" 
          [disabled]="availableTimeSlots().length === 0 || !selectedTimeSlot()"
          class="py-3 px-4 rounded-2xl bg-rose-500 text-white font-bold text-xs uppercase shadow-md shadow-rose-200 hover:bg-rose-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Ir a pagar
        </button>
      </div>
    </div>
  `
})
export class ProductBuyOptionsComponent {
  @Input({ required: true }) product!: ProductDetail;
  @Input() availableAddons: ProductAddon[] = [];
  @Output() buy = new EventEmitter<AddToCartPayload>();

  private cartService = inject(CartService);
  private carritoService = inject(CarritoService);
  private router = inject(Router);

  // 1. SIGNALS DE ESTADO
  dateOption = signal<'today' | 'tomorrow' | 'custom'>('today');
  customDate = signal<string>('');
  selectedTimeSlot = signal<string>('9am-1pm');
  quantity = signal<number>(1);
  selectedAddons = signal<ProductAddon[]>([]);

  // Configuración base de horarios con hora límite de compra (maxHour)
  readonly allTimeSlots: TimeSlotConfig[] = [
    { id: '9am-1pm', label: '9am - 1pm', maxHour: 12 },
    { id: '1pm-6pm', label: '1pm - 6pm', maxHour: 17 },
    { id: '6pm-9pm', label: '6pm - 9pm', maxHour: 20 }
  ];

  todayStr = this.formatDate(new Date());
  tomorrowStr = this.formatDate(new Date(Date.now() + 86400000));

  // 2. COMPUTED: Fecha seleccionada (YYYY-MM-DD)
  effectiveDate = computed(() => {
    switch (this.dateOption()) {
      case 'today': return this.todayStr;
      case 'tomorrow': return this.tomorrowStr;
      case 'custom': return this.customDate() || this.todayStr;
    }
  });

  // 3. COMPUTED: Horarios válidos según la hora actual
  availableTimeSlots = computed(() => {
    const isToday = this.effectiveDate() === this.todayStr;
    if (!isToday) return this.allTimeSlots;

    const currentHour = new Date().getHours();
    return this.allTimeSlots.filter(slot => currentHour < slot.maxHour);
  });

  // 4. COMPUTED: Cálculo de Precio Total Reactivo
  calculateTotal = computed(() => {
    const addonsTotal = this.selectedAddons().reduce((acc, curr) => acc + curr.price, 0);
    return (this.product.price + addonsTotal) * this.quantity();
  });

  // 5. EFFECT: Autoseleccionar el primer turno válido disponible si el seleccionado vence
  constructor() {
    effect(() => {
      const available = this.availableTimeSlots();
      const currentSlot = this.selectedTimeSlot();

      if (available.length > 0) {
        const isValid = available.some(s => s.id === currentSlot);
        if (!isValid) {
          this.selectedTimeSlot.set(available[0].id);
        }
      } else {
        this.selectedTimeSlot.set('');
      }
    });
  }

  // MÉTODOS Y VALIDACIONES
  selectDateOption(option: 'today' | 'tomorrow' | 'custom') {
    this.dateOption.set(option);
    if (option === 'custom' && !this.customDate()) {
      this.customDate.set(this.tomorrowStr);
    }
  }

  onCustomDateChange(value: string) {
    if (!value) return;

    // Validar que no sea una fecha pasada
    if (value < this.todayStr) {
      this.customDate.set(this.todayStr);
      this.dateOption.set('today');
      return;
    }
    console.log(value)
    console.log(this.tomorrowStr)

    // Si coincide con Hoy o Mañana, se cambia automáticamente la opción activa
    if (value === this.todayStr) {
      this.dateOption.set('today');
    } else if (value === this.tomorrowStr) {
      this.dateOption.set('tomorrow');
    } else {
      this.customDate.set(value);
      this.dateOption.set('custom');
    }
  }

  changeQuantity(delta: number) {
    if (this.quantity() + delta >= 1) {
      this.quantity.update(q => q + delta);
    }
  }

  private formatDate(d: Date): string {
    return d.toISOString().split('T')[0];
  }

  submitOrder() {
    this.buy.emit({
      productId: this.product.id,
      addons: this.selectedAddons(),
      deliveryDate: this.effectiveDate(),
      deliveryTimeSlot: this.selectedTimeSlot(),
      quantity: this.quantity(),
      totalPrice: this.calculateTotal()
    });
  }

  onAddToCart() {
    const payload: AddToCartPayload = {
      productId: this.product.id,
      addons: this.selectedAddons(),
      deliveryDate: this.effectiveDate(),
      deliveryTimeSlot: this.selectedTimeSlot(),
      quantity: this.quantity(),
      totalPrice: this.calculateTotal()
    };
    this.cartService.addToCart(this.product, payload);
    this.carritoService.show()
  }

  goToPay() {
    const payload: AddToCartPayload = {
      productId: this.product.id,
      addons: this.selectedAddons(),
      deliveryDate: this.effectiveDate(),
      deliveryTimeSlot: this.selectedTimeSlot(),
      quantity: this.quantity(),
      totalPrice: this.calculateTotal()
    };
    this.cartService.addToCart(this.product, payload, () => {
      this.router.navigate([Constante.URL_CHECKOUT]);
    });
  }
}